/**
 * lib/liveClassApi.js
 * --------------------
 * Thin axios wrapper for Sprint LMS-Live endpoints.  Keeps provider
 * names OUT of the frontend — the UI only ever sees Diplomexa-side
 * IDs + opaque tokens.
 */
import api, { formatApiError } from "./api";

export async function listLiveSessions(params = {}) {
  const r = await api.get("/lms/live-sessions", { params });
  return r.data.sessions || [];
}

export async function getLiveSession(sessionId) {
  const r = await api.get(`/lms/live-sessions/${sessionId}`);
  return r.data;
}

export async function createLiveSession(body) {
  const r = await api.post("/lms/live-sessions/create", body);
  return r.data;
}

export async function joinLiveSession(sessionId) {
  const r = await api.post(`/lms/live-sessions/${sessionId}/join`);
  return r.data;
}

export async function leaveLiveSession(sessionId) {
  const r = await api.post(`/lms/live-sessions/${sessionId}/leave`);
  return r.data;
}

export async function startRecording(sessionId) {
  const r = await api.post(`/lms/live-sessions/${sessionId}/start-recording`);
  return r.data;
}

export async function stopRecording(sessionId) {
  const r = await api.post(`/lms/live-sessions/${sessionId}/stop-recording`);
  return r.data;
}

export async function getRecordingStatus(sessionId) {
  const r = await api.get(`/lms/live-sessions/${sessionId}/recording`);
  return r.data;
}

export async function getRecordingPlayback(sessionId) {
  const r = await api.get(`/lms/live-sessions/${sessionId}/recording/playback`);
  return r.data;
}

export async function submitFeedback(sessionId, body) {
  const r = await api.post(`/lms/live-sessions/${sessionId}/feedback`, body);
  return r.data;
}

export async function getAttendance(sessionId) {
  const r = await api.get(`/lms/live-sessions/${sessionId}/attendance`);
  return r.data;
}

export async function trainerConsole() {
  const r = await api.get("/lms/live/trainer/console");
  return r.data;
}

export async function parentLiveSummary() {
  const r = await api.get("/lms/live/parent/summary");
  return r.data;
}

export async function adminProviderSettings() {
  const r = await api.get("/admin/lms/live/provider-settings");
  return r.data;
}

export async function reconcileRecordings() {
  const r = await api.post("/admin/lms/live-sessions/reconcile-recordings");
  return r.data;
}

// Premium tools — each returns { ok, consume:{cost,balance_after}, output:{brand,subtitle,content,...} }
export async function premiumLiveSummary(sessionId) {
  return (await api.post(`/lms/live-sessions/${sessionId}/premium/ai-summary`)).data;
}
export async function premiumTrainerReport(sessionId) {
  return (await api.post(`/lms/live-sessions/${sessionId}/premium/trainer-report`)).data;
}
export async function premiumParentReport(sessionId) {
  return (await api.post(`/lms/live-sessions/${sessionId}/premium/parent-report`)).data;
}
export async function premiumAttendanceCert(sessionId) {
  return (await api.post(`/lms/live-sessions/${sessionId}/premium/attendance-certificate`)).data;
}
export async function premiumSessionNotes(sessionId) {
  return (await api.post(`/lms/live-sessions/${sessionId}/premium/session-notes`)).data;
}

// "Ask Diplomexa Live" — in-class learner overlay; structured live notes.
// `focus` is an optional learner-typed prompt ("what should I prepare?").
export async function premiumInClassNotes(sessionId, focus = null) {
  const body = focus && focus.trim() ? { focus: focus.trim() } : {};
  return (
    await api.post(
      `/lms/live-sessions/${sessionId}/premium/in-class-notes`, body,
    )
  ).data;
}

// Live Note History — calling user's own past notes for this session.
export async function getMyLiveNotes(sessionId, limit = 30) {
  return (
    await api.get(
      `/lms/live-sessions/${sessionId}/my-live-notes?limit=${limit}`,
    )
  ).data;
}

// Trainer/admin aggregator — all learners' notes for this session.
export async function getAllLiveNotes(sessionId, limit = 200) {
  return (
    await api.get(
      `/lms/live-sessions/${sessionId}/all-live-notes?limit=${limit}`,
    )
  ).data;
}

// ─────────────────────── Polls inside Live Classroom ──────────────────
export async function listSessionPolls(sessionId) {
  return (await api.get(`/lms/live-sessions/${sessionId}/polls`)).data;
}
export async function createSessionPoll(sessionId, body) {
  return (await api.post(`/lms/live-sessions/${sessionId}/polls`, body)).data;
}
export async function launchSessionPoll(pollId) {
  return (await api.post(`/lms/live-sessions/polls/${pollId}/launch`)).data;
}
export async function closeSessionPoll(pollId) {
  return (await api.post(`/lms/live-sessions/polls/${pollId}/close`)).data;
}
export async function voteSessionPoll(pollId, option) {
  return (
    await api.post(`/lms/live-sessions/polls/${pollId}/vote`, { option })
  ).data;
}
export async function getSessionPollResults(pollId) {
  return (await api.get(`/lms/live-sessions/polls/${pollId}/results`)).data;
}

// ─────────────────── Quizzes inside Live Classroom ────────────────────
export async function listSessionQuizzes(sessionId) {
  return (await api.get(`/lms/live-sessions/${sessionId}/quizzes`)).data;
}
export async function createSessionQuiz(sessionId, body) {
  return (await api.post(`/lms/live-sessions/${sessionId}/quizzes`, body)).data;
}
export async function launchSessionQuiz(quizId) {
  return (await api.post(`/lms/live-sessions/quizzes/${quizId}/launch`)).data;
}
export async function closeSessionQuiz(quizId) {
  return (await api.post(`/lms/live-sessions/quizzes/${quizId}/close`)).data;
}
export async function answerSessionQuiz(quizId, questionId, choice) {
  return (
    await api.post(`/lms/live-sessions/quizzes/${quizId}/answer`, {
      question_id: questionId,
      choice,
    })
  ).data;
}
export async function getSessionQuizLeaderboard(quizId) {
  return (
    await api.get(`/lms/live-sessions/quizzes/${quizId}/leaderboard`)
  ).data;
}

export { formatApiError };
